import { Spinner } from '../components/feedback';
import { HoldToConfirm } from '../components/HoldToConfirm';
import { useState } from 'react';
import { WorldGenerationSchema, addonKindsOf, type RegenerateWorld } from '@mcpanel/core';
import { api } from '../api/client';
import type { ChangeRequest, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { WorldGenerationFields } from '../components/WorldGenerationFields';
import { ErrorAlert } from '../components/ui';
import { Accordion } from '../components/Accordion';
import { usePoll } from '../hooks/usePoll';
import { ReviewDialog } from './ServerManage';

/** 再生成で引き継がれない・ずれるもの。ソフトの種類で出し分ける */
export function RegenerateNotes({ server, preservePlayers }: { server: ServerView; preservePlayers: boolean }) {
  const kinds = addonKindsOf(server.software.family);
  const notes = [
    'ゲームルール・ワールドボーダーは新しいワールドの初期値に戻ります。',
    'data/ の地図・スコアボードは消えます。手持ちの地図は白紙か別の地図になり、スコアを使うデータパックの記録も消えます。',
  ];
  if (preservePlayers)
    notes.push('プレイヤーの位置とベッドは古い座標のままです。新しい地形で埋まったり落ちたりすることがあります。');
  if (kinds.plugins)
    notes.push('plugins/ のデータ（家・土地・ワープなど）は残り、古い地形の座標を指したままになります。');
  if (kinds.mods)
    notes.push(
      'Mod がワールドの中に置くデータ（クエストの進み具合など）は消えます。Mod の設定（serverconfig）は残します。',
    );
  return (
    <div className="alert warn small">
      <strong>引き継がれないもの</strong>
      <ul>
        {notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </div>
  );
}

function Editor({ server, view }: { server: ServerView; view: Awaited<ReturnType<typeof api.worldGeneration>> }) {
  const { canOperate, notify } = useApp();
  const [value, setValue] = useState(view.pending ?? view.current);
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [options, setOptions] = useState<RegenerateWorld>({
    confirmName: server.name,
    preservePlayers: true,
    seedMode: 'random',
  });
  async function plan(regenerate: boolean) {
    setBusy(true);
    try {
      setRequest(
        await api.createRequest(
          regenerate ? 'regenerate_world' : 'change_world_generation',
          regenerate ? { ...options, confirmName: server.name } : WorldGenerationSchema.parse(value),
          server.id,
        ),
      );
      setConfirming(false);
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="stack">
      <p>保存した生成設定は次にワールドを再生成するときに使います。今のワールドの地形は変わりません。</p>
      <Accordion title="今の生成設定（server.properties）" storageKey={`server:${server.id}:world-properties`}>
        <pre>{JSON.stringify(view.properties, null, 2)}</pre>
      </Accordion>
      <fieldset disabled={!canOperate || busy} style={{ border: 0, padding: 0 }}>
        <WorldGenerationFields value={value} onChange={setValue} mods={[]} candidates={view.customWorldTypes} />
        <button
          className="btn primary"
          disabled={
            !WorldGenerationSchema.safeParse(value).success ||
            JSON.stringify(value) === JSON.stringify(view.pending ?? view.current)
          }
          onClick={() => void plan(false)}
        >
          生成設定の変更内容を確認
        </button>
      </fieldset>
      <div className="card card-body stack">
        <h3>生成を変える Mod・データパック</h3>
        {view.modifiers.length ? (
          view.modifiers.map((mod) => (
            <p key={mod.label}>
              {mod.label}: {mod.message}
            </p>
          ))
        ) : (
          <p>確認済みの生成変更 Mod・データパックはありません。手入力の生成方法 ID も使えます。</p>
        )}
        <p>導入済みデータパック: {view.datapacks.join('、') || 'なし'}</p>
      </div>
      {canOperate && (
        <button data-search-id="regenerate" className="btn danger" disabled={busy} onClick={() => setConfirming(true)}>
          {busy && <Spinner />}
          ワールドを再生成
        </button>
      )}
      {confirming && (
        <section role="dialog" aria-modal="true" aria-label="再生成の確認" className="card card-body stack">
          <h2>ワールドを再生成</h2>
          <p>
            現世・ネザー・エンドを作り直します。自動でスナップショットを取ります。元に戻すにはスナップショットから復元してください。統合版や
            plugins/ のデータは残します。
          </p>
          <p>この操作は保存済みの生成設定を使います。上の未保存の変更は使いません。</p>
          <label className="check">
            <input
              type="checkbox"
              checked={options.preservePlayers}
              onChange={(e) => setOptions({ ...options, preservePlayers: e.target.checked })}
            />
            プレイヤーデータ・統計・進捗を残す（外すと初期化）
          </label>
          <RegenerateNotes server={server} preservePlayers={options.preservePlayers} />
          <label>
            シード
            <select
              className="input"
              value={options.seedMode}
              onChange={(e) => setOptions({ ...options, seedMode: e.target.value as RegenerateWorld['seedMode'] })}
            >
              <option value="random">空欄にして新しく決める</option>
              <option value="current">今のシードを使う</option>
              <option value="specified">指定する</option>
            </select>
          </label>
          {options.seedMode === 'specified' && (
            <label>
              指定シード
              <input
                className="input"
                value={options.seed ?? ''}
                maxLength={200}
                onChange={(e) => setOptions({ ...options, seed: e.target.value })}
              />
            </label>
          )}
          <HoldToConfirm
            disabled={busy || (options.seedMode === 'specified' && !options.seed)}
            onConfirm={() => void plan(true)}
          >
            計画を確認して実行へ
          </HoldToConfirm>
          <button className="btn" onClick={() => setConfirming(false)}>
            キャンセル
          </button>
        </section>
      )}
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </section>
  );
}
export function WorldGenerationSection({ server }: { server: ServerView }) {
  const result = usePoll(() => api.worldGeneration(server.id), 10000, [server.id, server.revision]);
  return (
    <>
      <ErrorAlert error={result.error} />
      {result.data && <Editor key={JSON.stringify(result.data)} server={server} view={result.data} />}
    </>
  );
}
